import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { BLOCK_TYPE_LABELS } from "@/lib/homeContent";
import { Field, VisibleSwitch, Card } from "@/components/admin/CmsFields";

const TitleFields = ({ b, set }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    <Field label="Başlık (ops.)" value={b.title} max={80} onChange={(v) => set({ title: v })} testid={`cms-block-title-${b.id}`} />
    <Field label="Alt açıklama (ops.)" value={b.subtitle} max={200} onChange={(v) => set({ subtitle: v })} testid={`cms-block-subtitle-${b.id}`} />
  </div>
);

const ItemList = ({ b, set, max, empty, render }) => {
  const upd = (i, patch) => set({ items: b.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  return (
    <div className="space-y-2">
      {b.items.map((it, i) => (
        <div key={i} className="rounded-xl bg-muted/30 p-3 flex gap-2 items-start" data-testid={`cms-block-item-${b.id}-${i}`}>
          <div className="flex-1 min-w-0 space-y-2">{render(it, (p) => upd(i, p), i)}</div>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" disabled={b.items.length === 1}
            onClick={() => set({ items: b.items.filter((_, j) => j !== i) })} data-testid={`cms-block-item-remove-${b.id}-${i}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" disabled={b.items.length >= max} onClick={() => set({ items: [...b.items, empty] })} data-testid={`cms-block-item-add-${b.id}`}>
        <Plus className="w-4 h-4 mr-1" />Ekle ({b.items.length}/{max})
      </Button>
    </div>
  );
};

const FaqEditor = ({ b, set }) => (
  <>
    <TitleFields b={b} set={set} />
    <ItemList b={b} set={set} max={30} empty={{ q: "", a: "" }} render={(it, u, i) => (<>
      <Field label={`Soru ${i + 1}`} value={it.q} max={200} onChange={(v) => u({ q: v })} testid={`cms-faq-q-${b.id}-${i}`} />
      <Field label="Cevap" area value={it.a} max={1000} onChange={(v) => u({ a: v })} testid={`cms-faq-a-${b.id}-${i}`} />
    </>)} />
  </>
);

const StatsEditor = ({ b, set }) => (
  <>
    <TitleFields b={b} set={set} />
    <ItemList b={b} set={set} max={8} empty={{ value: "", label: "" }} render={(it, u, i) => (
      <div className="grid grid-cols-[120px_1fr] gap-2">
        <Field label="Değer" value={it.value} max={20} onChange={(v) => u({ value: v })} testid={`cms-stat-value-${b.id}-${i}`} />
        <Field label="Açıklama" value={it.label} max={60} onChange={(v) => u({ label: v })} testid={`cms-stat-label-${b.id}-${i}`} />
      </div>
    )} />
    <p className="text-[11px] text-muted-foreground">Rakamları siz girersiniz; sistem otomatik veya örnek rakam üretmez. Örn: “81”, “7/24”, “%98”.</p>
  </>
);

const ColumnsEditor = ({ b, set }) => {
  const setCount = (n) => set({ columns: Array.from({ length: n }, (_, i) => b.columns[i] || { html: "" }) });
  return (
    <>
      <TitleFields b={b} set={set} />
      <div className="flex items-center gap-2 text-xs">Sütun sayısı:
        {[1, 2, 3].map((n) => <Button key={n} size="sm" variant={b.columns.length === n ? "default" : "outline"} onClick={() => setCount(n)} data-testid={`cms-columns-count-${b.id}-${n}`}>{n}</Button>)}
      </div>
      <div className={`grid gap-2 ${b.columns.length > 1 ? "md:grid-cols-2" : ""} ${b.columns.length > 2 ? "xl:grid-cols-3" : ""}`}>
        {b.columns.map((c, i) => <Field key={i} label={`Sütun ${i + 1} (HTML)`} area value={c.html} max={5000}
          onChange={(v) => set({ columns: b.columns.map((x, j) => (j === i ? { html: v } : x)) })} testid={`cms-column-html-${b.id}-${i}`} />)}
      </div>
      <p className="text-[11px] text-muted-foreground">İzinli etiketler: p, br, strong, em, u, ul, ol, li, h3, h4, blockquote, a (yalnız https). Diğer her şey (script, stil, resim, iframe) kaydedilirken otomatik temizlenir.</p>
    </>
  );
};

const AdEditor = ({ b, set }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file.type.startsWith("image/")) return toast.error("Yalnızca görsel yüklenebilir");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ image_url: data.url }); toast.success("Görsel yüklendi");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const src = b.image_url && (b.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${b.image_url}` : b.image_url);
  return (
    <>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Reklam adı (yalnız panelde görünür)" value={b.label} max={60} onChange={(v) => set({ label: v })} testid={`cms-ad-label-${b.id}`} />
        <Field label="Görsel açıklaması (erişilebilirlik)" value={b.alt_text} max={120} onChange={(v) => set({ alt_text: v })} testid={`cms-ad-alt-${b.id}`} />
      </div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
        <Field label="Görsel (https:// adresi veya yükleyin)" value={b.image_url} max={500} onChange={(v) => set({ image_url: v })} testid={`cms-ad-image-${b.id}`} />
        <label className="inline-flex items-center justify-center h-10 px-3 rounded-md border text-sm cursor-pointer hover:bg-muted">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Yükle
          <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid={`cms-ad-upload-${b.id}`} />
        </label>
      </div>
      {src && <img src={src} alt="" className="max-h-32 rounded-xl border object-cover" data-testid={`cms-ad-thumb-${b.id}`} />}
      <Field label="Tıklanınca gidilecek bağlantı (ops.)" value={b.link_url} max={300} onChange={(v) => set({ link_url: v })} testid={`cms-ad-link-${b.id}`} hint="Örn: /tasima-yaptir veya https://..." />
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Başlangıç (ops.)</Label><Input type="date" className="mt-1" value={b.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid={`cms-ad-start-${b.id}`} /></div>
        <div><Label className="text-xs">Bitiş (ops.)</Label><Input type="date" className="mt-1" value={b.end_date} onChange={(e) => set({ end_date: e.target.value })} data-testid={`cms-ad-end-${b.id}`} /></div>
      </div>
      <p className="text-[11px] text-muted-foreground">Tarih aralığı dışında reklam ana sayfada gösterilmez (Türkiye saati). Sayfada küçük bir “Reklam” etiketi bulunur.</p>
    </>
  );
};

const EDITORS = { faq: FaqEditor, stats: StatsEditor, columns: ColumnsEditor, ad: AdEditor };

export const BlockEditor = ({ b, set, onRemove }) => {
  const E = EDITORS[b.type];
  return (
    <Card title={BLOCK_TYPE_LABELS[b.type]} testid={`cms-block-${b.id}`} right={
      <div className="flex items-center gap-2">
        <VisibleSwitch checked={b.visible} onChange={(v) => set({ visible: v })} testid={`cms-block-visible-${b.id}`} />
        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={onRemove} data-testid={`cms-block-remove-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>}>
      <E b={b} set={set} />
    </Card>
  );
};

export const AddBlockBar = ({ onAdd, disabled }) => (
  <div className="rounded-2xl border border-dashed p-3 flex flex-wrap items-center gap-2" data-testid="cms-add-block">
    <span className="text-sm font-medium mr-1">Bölüm ekle:</span>
    {Object.entries(BLOCK_TYPE_LABELS).map(([t, l]) => (
      <Button key={t} size="sm" variant="outline" disabled={disabled} onClick={() => onAdd(t)} data-testid={`cms-add-block-${t}`}><Plus className="w-4 h-4 mr-1" />{l}</Button>
    ))}
  </div>
);
